import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { PageSEO } from "@/components/PageSEO";
import { API } from "@/lib/constants";

/* Pagina dell'incasso del mese: una sola domanda, poi il risultato con il calendario.
   Si apre solo con il codice personale; se il codice non esiste la pagina è identica a una 404. */

const eur = (v) => `${String(Math.round(v || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")} €`;
const dataIt = (s) => new Date(s).toLocaleDateString("it-IT");

const QmdpIncassoPage = () => {
  const { codice } = useParams();
  const [stato, setStato] = useState("caricamento");
  const [info, setInfo] = useState(null);
  const [incasso, setIncasso] = useState("");
  const [res, setRes] = useState(null);
  const [invio, setInvio] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/qmdp/${codice}`)
      .then(({ data }) => { setInfo(data); setRes(data.ultimo_calcolo || null); setStato("ok"); })
      .catch(() => setStato("non-trovata"));
  }, [codice]);

  const calcola = async () => {
    setErr(null); setInvio(true);
    try {
      const { data } = await axios.post(`${API}/qmdp/${codice}/incasso`, { incasso: Number(incasso) });
      setRes(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(e.response?.data?.detail || "Non siamo riusciti a calcolare: riprova tra poco.");
    } finally { setInvio(false); }
  };

  if (stato === "caricamento") return <main className="pt-32 pb-20 min-h-screen bg-[#FAF8F5]" />;

  if (stato === "non-trovata") {
    return (
      <main className="pt-32 pb-24 min-h-screen bg-[#FAF8F5]" data-testid="qmdp-incasso-non-trovata">
        <PageSEO title="Pagina non trovata | SC Dottori Commercialisti" description="Pagina non trovata." noindex />
        <div className="max-w-xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl text-[#0D1527] mb-4">PAGINA NON TROVATA</h1>
          <p className="text-[#4A5568]">
            Il link potrebbe essere incompleto. Apri il link dall'ultima email che ti abbiamo mandato oppure scrivi a{" "}
            <a className="text-[#8A6A28] underline" href="mailto:assistenza@casalisilvia.com">assistenza@casalisilvia.com</a>.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-32 pb-24 min-h-screen bg-[#FAF8F5]" data-testid="qmdp-incasso-page">
      <PageSEO title="Quanto hai incassato | Quanto Mettere da Parte" description="Pagina personale del servizio Quanto Mettere da Parte." noindex />
      <div className="max-w-2xl mx-auto px-6">
        <p className="text-[#8A6A28] text-xs tracking-[0.3em] mb-4">QUANTO METTERE DA PARTE</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-[#0D1527] leading-tight">
          {info.mese_nome
            ? <>QUANTO HAI INCASSATO A <span className="text-[#2F7DBF]">{(info.mese_nome || "").toUpperCase()}</span>?</>
            : <>NESSUN MESE DA <span className="text-[#2F7DBF]">DICHIARARE</span></>}
        </h1>
        {info.mese_nome ? (
          <p className="text-[#4A5568] mt-4">
            {info.nome ? `Ciao ${info.nome}, ` : ""}
            {info.correzione
              ? `per ${info.mese_nome} hai già risposto: se l'importo è cambiato, scrivilo qui sotto e rifaccio il conteggio.`
              : `dimmi quanto hai incassato a ${info.mese_nome} e ti dico subito quanto mettere da parte. Se non hai incassato nulla, scrivi 0.`}
          </p>
        ) : (
          <p className="text-[#4A5568] mt-4" data-testid="qmdp-nessun-mese">
            Hai già dichiarato tutti i mesi conclusi. La prossima domanda ti arriva il primo giorno del mese.
          </p>
        )}

        {!info.scheda_compilata && (
          <div className="mt-6 bg-[#FFF6E0] border border-[#E2C275] rounded p-4 text-[#6B531A]" data-testid="qmdp-scheda-mancante">
            La tua scheda non è ancora compilata: il calcolo sarà approssimativo.{" "}
            <Link className="underline" to={`/quanto-mettere-da-parte/scheda/${codice}`}>Compilala adesso</Link>.
          </div>
        )}

        {info.mese_nome && (
        <div className="mt-8 bg-white border border-[#E8E2D5] rounded-lg p-6">
          <label className="block">
            <span className="text-[#64708A] text-xs tracking-wide">Incassato a {info.mese_nome}</span>
            <input type="number" value={incasso} onChange={(e) => setIncasso(e.target.value)} data-testid="incasso-input"
              className="w-full mt-1 bg-white border border-[#D9D2C5] rounded px-3 py-3 text-[#0D1527] text-lg" placeholder="0" />
          </label>
          {err && <p className="text-red-600 text-sm mt-3" data-testid="qmdp-incasso-errore">{err}</p>}
          <button onClick={calcola} disabled={invio || incasso === ""} data-testid="incasso-calcola"
            className="mt-5 px-8 py-3.5 rounded bg-[#2F7DBF] text-white font-bold tracking-wide hover:bg-[#1F4E75] transition-colors disabled:opacity-40">
            {invio ? "CALCOLO…" : "CALCOLA"}
          </button>
          {info.gia_risposto && !res && (
            <p className="text-[#8E9AAE] text-xs mt-3">Per questo mese hai già risposto: se reinvii, il conteggio viene aggiornato.</p>
          )}
        </div>
        )}

        {res && (
          <div className="mt-10" data-testid="qmdp-incasso-risultato">
            <div className="bg-white border-2 border-[#3E8ED0] rounded-lg p-8 text-center">
              <p className="text-[#64708A] text-xs tracking-[0.25em]">QUESTO MESE METTI DA PARTE</p>
              <p className="font-heading text-5xl text-[#2F7DBF] my-3" data-testid="incasso-quota">{eur(res.quota_mese)}</p>
              <p className="text-[#4A5568] text-sm">
                su 100 € incassati, {res.percentuale_testo} € servono per tasse e contributi
              </p>
            </div>
            <p className="text-[#0D1527] text-base md:text-lg mt-5" data-testid="incasso-resto">{res.resto_testo}</p>
            <p className="text-[#4A5568] mt-6">
              Da inizio anno hai incassato <b className="text-[#0D1527]">{eur(res.incassato_da_inizio_anno)}</b> e dovresti
              avere da parte <b className="text-[#0D1527]">{eur(res.dovrebbe_avere_da_parte)}</b>.
            </p>

            <h2 className="text-[#0D1527] text-base md:text-lg font-bold mt-8 mb-2">Prossimi pagamenti</h2>
            <table className="w-full text-sm" data-testid="incasso-calendario">
              <tbody>
                {res.calendario.map((v, i) => (
                  <tr key={i} className="border-b border-[#E8E2D5]">
                    <td className="py-2 text-[#64708A] font-mono text-xs whitespace-nowrap">{dataIt(v.data)}</td>
                    <td className="py-2 text-[#0D1527]">{v.descrizione}</td>
                    <td className="py-2 text-right text-[#2F7DBF] font-mono whitespace-nowrap">
                      {v.credito ? `a credito ${eur(v.credito)}` : eur(v.importo)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {res.avvisi?.length > 0 && (
              <div className="mt-6 bg-[#FFF6E0] border border-[#E2C275] rounded p-4" data-testid="incasso-avvisi">
                {res.avvisi.map((a, i) => <p key={i} className="text-[#6B531A] text-sm">{a}</p>)}
              </div>
            )}

            <p className="text-[#8E9AAE] text-xs mt-8">
              Le cifre sono calcolate in automatico sui dati che ci hai dato. Se qualcosa è cambiato,{" "}
              <Link className="underline" to={`/quanto-mettere-da-parte/scheda/${codice}`}>aggiorna la tua scheda</Link>.
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

export default QmdpIncassoPage;
